import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { CollectionService } from '../../../core/services/collection.service';
import { NavigationService } from '../../../core/services/navigation.service';
import { ScrollService } from '../../../core/services/scroll.service';
import { LoggingService } from '../../../core/services/logging.service';
import { YardiePicksTabsService } from '../../../core/services/yardie-picks-tabs.service';
import { StockTableComponent, TableSortEvent } from '../../../shared/ui/tables/stock-table/stock-table.component';
import { Candidate } from '../../../shared/models/candidate-model';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

const PICKS_BASE_PATH = '/picks';

@Component({
  standalone: true,
  selector: 'app-stock-list',
  imports: [
    CommonModule,
    StockTableComponent
  ],
  template: `
    <app-stock-table
      [stocks]="collections()"
      [isLoading]="isLoading()"
      [sortField]="sortField()"
      [sortOrder]="sortOrder()"
      (stockSelected)="onStockSelected($event)"
      (sortChanged)="onSortChanged($event)">
    </app-stock-table>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StockListComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private collectionService = inject(CollectionService);
  private navigationService = inject(NavigationService);
  private scrollService = inject(ScrollService);
  private logger = inject(LoggingService);
  private yardiePicksTabsService = inject(YardiePicksTabsService);
  private destroy$ = new Subject<void>();
  private collectionSubscription?: Subscription;
  private pendingScrollRestoreTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Active collection name from route (lowercase URL form)
  private activeCollectionRoute = '';

  // Collections data
  collections = signal<Candidate[]>([]);
  isLoading = signal<boolean>(false);

  // Sort state
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number | undefined>(undefined);

  /** Route key scoped to the active collection tab (e.g. /picks/toppatop) */
  private get routeKey(): string {
    return `${PICKS_BASE_PATH}/${this.activeCollectionRoute}`;
  }

  ngOnInit(): void {
    // Subscribe to route parameter changes
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const collectionNameFromRoute = params.get('collectionName');
        if (collectionNameFromRoute) {
          this.activeCollectionRoute = collectionNameFromRoute.toLowerCase();

          // Restore sort state for current route
          const savedState = this.navigationService.getListState(this.routeKey);
          this.sortField.set(savedState?.sortField);
          this.sortOrder.set(savedState?.sortOrder);

          // Convert URL format to proper case format for API
          const collectionName = this.convertUrlToCollectionName(collectionNameFromRoute);
          this.loadCollection(collectionName);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearPendingScrollRestore();
  }

  // Convert URL format (lowercase) to proper collection name format
  private convertUrlToCollectionName(urlFormat: string): string {
    return this.yardiePicksTabsService.getTabs().find(
      tab => tab.collectionName.toLowerCase() === urlFormat.toLowerCase()
    )?.collectionName ?? urlFormat;
  }

  // Load collection data
  loadCollection(collectionName: string): void {
    this.collectionSubscription?.unsubscribe();
    this.clearPendingScrollRestore();
    this.collections.set([]);
    this.isLoading.set(true);
    // Capture at call time so the async callback uses the correct key
    const currentRouteKey = this.routeKey;
    this.collectionSubscription = this.collectionService.getCollection(collectionName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.collections.set(data);
          this.isLoading.set(false);
          // Restore scroll position when returning from a stock detail page
          this.clearPendingScrollRestore();
          this.pendingScrollRestoreTimeoutId = this.scrollService.consumeAndScheduleRestore(currentRouteKey);
        },
        error: (error) => {
          this.logger.error('Error loading collection:', error);
          this.collections.set([]);
          this.isLoading.set(false);
        }
      });
  }

  private clearPendingScrollRestore(): void {
    if (this.pendingScrollRestoreTimeoutId !== null) {
      clearTimeout(this.pendingScrollRestoreTimeoutId);
      this.pendingScrollRestoreTimeoutId = null;
    }
  }

  // Handle stock selection and navigation
  onStockSelected(stock: Candidate): void {
    // Save scroll position so it can be restored when the user navigates back
    this.scrollService.savePendingRestore(this.routeKey, this.scrollService.getScrollTop());
    const collectionName = this.convertUrlToCollectionName(this.activeCollectionRoute);
    const listTitle = this.yardiePicksTabsService.getTabByCollectionName(collectionName)?.label ?? collectionName;
    this.navigationService.saveStockDetailListContext({
      sourceRoute: this.routeKey,
      sourceType: 'picks',
      listTitle,
      selectedTicker: stock.ticker,
      items: this.collections().map(candidate => ({
        ticker: candidate.ticker,
        name: candidate.name,
        discount: candidate.discount,
        currentPrice: candidate.currentPrice,
        stickerPrice: candidate.stickerPrice
      }))
    });
    this.router.navigate(['/stocks', stock.ticker]);
  }

  // Handle sort changes from table
  onSortChanged(event: TableSortEvent): void {
    this.sortField.set(event.field);
    this.sortOrder.set(event.order);

    // Save sort state keyed to the active collection tab
    this.navigationService.saveListState(this.routeKey, {
      sortField: event.field,
      sortOrder: event.order
    });
  }
}
